import { useMutation, useQuery } from "@tanstack/react-query";
import { AvForm, AvField } from "availity-reactstrap-validation";
import { withNamespaces } from "react-i18next";
import { toast } from "react-toastify";
import {
    Button,
    Spinner,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Row,
    Col,
    Label
} from "reactstrap";
import Select from "react-select";
import OtherAssetProcessingActivityService from "src/modules/data-protection/apis/OtherAssetProcessingActivityService";
import CommonService from "src/modules/data-protection/apis/CommonService";
import { useEffect, useState } from "react";

const EditModal = ({
    t,
    toggleModal,
    modalIsOpen,
    oldData,
    asset,
    onEditSuccess
}) => {
    const [selectedResponsiblePerson, setSelectedResponsiblePerson] = useState(null);
    const [activityUpdateIsLoading, setActivityUpdateIsLoading] = useState(false)
    const {
        mutateAsync: updateActivityTitle
    } = useMutation({
        mutationFn: async (payload) => {
            const service = OtherAssetProcessingActivityService.getInstance();
            return await service.updateActivity(payload)
        }
    });

    const {
        mutateAsync: updateActivityResponsiblePerson
    } = useMutation({
        mutationFn: async (payload) => {
            const service = OtherAssetProcessingActivityService.getInstance();
            return await service.updateActivityResponsiblePerson(payload)
        }
    });

    const {
        data: responsiblePeopleList,
        isFetching: responsiblePeopleListIsFetching
    } = useQuery({
        queryKey: ['gdpr-fetch-tasks-owners-list'],
        queryFn: async () => {
            const service = CommonService.getInstance();

            const result = await service.fetchTaskOwners();

            return (result || []).map((u) => {
                return {
                    value: u.id,
                    label: `${u.first_name} ${u.last_name}`,
                };
            })
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: (error) => {
            if (process.env.NODE_ENV === 'development') console.error(error);

            toast(t("An error occurred while fetching users."), {
                type: "error",
            });
        },
    });

    useEffect(() => {
        if (oldData?.analyst_assigned?.id) {
            setSelectedResponsiblePerson({
                value: oldData.analyst_assigned.id,
                label: `${oldData.analyst_assigned.first_name} ${oldData.analyst_assigned.last_name}`
            });
        }
        else {
            if (asset?.responsible_person) {
                setSelectedResponsiblePerson({
                    value: asset.responsible_person.id,
                    label: asset.responsible_person.full_name
                });
            }
        }
    }, [oldData]);

    useEffect(() => {
        if (!modalIsOpen) {
            setSelectedResponsiblePerson(null);
        }
    }, [modalIsOpen]);

    const handleSubmit = (_e, values) => {
        const step1 = updateActivityTitle({
            moduleName: values.moduleName,
            reportCase: oldData.case_id
        })

        const step2 = updateActivityResponsiblePerson({
            responsiblePerson: values.responsiblePerson,
            reportCase: oldData.case_id
        })

        setActivityUpdateIsLoading(true);

        Promise.all([step1, step2]).then(() => {
            toast(t('Processing activity updated successfully.'), {
                type: "success"
            })
            onEditSuccess && onEditSuccess();
            toggleModal();
        }).catch(() => {
            toast(t('Failed to update the processing activity. Please try again.'), {
                type: "error"
            })
        }).finally(() => {
            setActivityUpdateIsLoading(false)
        });
    }

    return (
        <Modal
            isOpen={modalIsOpen}
            toggle={toggleModal}
        >
            <AvForm
                className="needs-validation"
                onValidSubmit={handleSubmit}
            >
                <ModalHeader
                    toggle={toggleModal}
                >
                    {t("Edit Processing Activity")}
                </ModalHeader>
                <ModalBody>
                    <Row>
                        <Col sm="12" className="mb-3">
                            <Label htmlFor="activity-title-input">
                                {t("Title")}
                            </Label>
                            <AvField
                                id="activity-title-input"
                                name="moduleName"
                                type="text"
                                errorMessage={t("This field cannot be blank")}
                                className="form-control"
                                validate={{
                                    required: { value: true }
                                }}
                                defaultValue={oldData?.module_name}
                            />
                        </Col>

                        <Col sm="12">
                            <Label htmlFor="activity-responsible-person-input">
                                {t("Responsible Person")}
                            </Label>
                            <Select
                                id="activity-responsible-person-input"
                                placeholder={t("Select") + "..."}
                                classNamePrefix="select2-selection"
                                options={responsiblePeopleList}
                                menuPortalTarget={document.body}
                                isLoading={responsiblePeopleListIsFetching}
                                isDisabled={responsiblePeopleListIsFetching}
                                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                onChange={(e) => setSelectedResponsiblePerson(e)}
                                value={selectedResponsiblePerson}
                            />
                            <AvField
                                type="hidden"
                                name="responsiblePerson"
                                value={selectedResponsiblePerson?.value}
                                validate={{
                                    required: { value: true }
                                }}
                                errorMessage={t("Please select a responsible person for this activity")}
                            />
                        </Col>
                    </Row>
                </ModalBody>

                <ModalFooter>
                    <Button
                        color="secondary"
                        onClick={toggleModal}
                        className="me-2"
                    >
                        {t("Cancel")}
                    </Button>

                    <Button
                        color="primary"
                        disabled={activityUpdateIsLoading}
                    >
                        {activityUpdateIsLoading && (
                            <Spinner
                                size='sm'
                                className="me-1"
                            />
                        )}
                        {t("Save")}
                    </Button>
                </ModalFooter>
            </AvForm>
        </Modal>
    )
}

export default withNamespaces()(
    EditModal
);